import { Transaction } from "@shared/schema";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { AlertTriangle, MoreHorizontal, Pencil, Trash, Split, EyeOff, Eye } from "lucide-react";
import { AnomalyDetectionButton } from "@/components/transactions/anomaly-detection";

interface TransactionActionsProps {
  transaction: Transaction;
  onEdit?: (transaction: Transaction) => void;
  onDelete?: (transaction: Transaction) => void;
  onSplit?: (transaction: Transaction) => void;
  onExclude?: (transaction: Transaction) => void;
  onRestore?: (transaction: Transaction) => void;
  isPendingEdit?: boolean;
  isPendingDelete?: boolean;
  isPendingSplit?: boolean;
  isPendingExclude?: boolean;
  isPendingRestore?: boolean;
  showAnomalyDetection?: boolean;
  size?: "default" | "sm";
}

export function TransactionActions({ 
  transaction, 
  onEdit, 
  onDelete,
  onSplit,
  onExclude,
  onRestore,
  isPendingEdit,
  isPendingDelete,
  isPendingSplit,
  isPendingExclude,
  isPendingRestore,
  showAnomalyDetection = true,
  size = "default"
}: TransactionActionsProps) {
  return (
    <div className="flex items-center justify-end space-x-1">
      {showAnomalyDetection && (
        <AnomalyDetectionButton transaction={transaction} size={size} />
      )}
      
      {(onEdit || onDelete || onExclude || onRestore) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size={size === "sm" ? "sm" : "icon"} 
              className={size === "sm" ? "h-7 w-7 p-0" : ""}
            >
              <MoreHorizontal className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={size === "sm" ? "w-32" : ""}>
            {onEdit && (
              <DropdownMenuItem 
                onClick={() => onEdit(transaction)}
                disabled={isPendingEdit}
                className={size === "sm" ? "py-1.5 text-xs" : ""}
              >
                <Pencil className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4 mr-2"} />
                Edit
              </DropdownMenuItem>
            )}
            {onSplit && (
              <DropdownMenuItem 
                onClick={() => onSplit(transaction)}
                disabled={isPendingSplit}
                className={size === "sm" ? "py-1.5 text-xs" : ""}
              >
                <Split className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4 mr-2"} />
                Split Expense
              </DropdownMenuItem>
            )}
            
            {/* Exclude/Restore options */}
            {!transaction.excluded && onExclude && (
              <DropdownMenuItem 
                onClick={() => onExclude(transaction)}
                disabled={isPendingExclude}
                className={size === "sm" ? "py-1.5 text-xs" : ""}
              >
                <EyeOff className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4 mr-2"} />
                Exclude
              </DropdownMenuItem>
            )}
            {transaction.excluded === true && onRestore && (
              <DropdownMenuItem 
                onClick={() => onRestore(transaction)}
                disabled={isPendingRestore}
                className={size === "sm" ? "py-1.5 text-xs" : ""}
              >
                <Eye className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4 mr-2"} />
                Restore
              </DropdownMenuItem>
            )}
            
            {/* Add separator before Delete */}
            {onDelete && <DropdownMenuSeparator />}
            
            {onDelete && (
              <DropdownMenuItem 
                onClick={() => onDelete(transaction)}
                className={`text-destructive ${size === "sm" ? "py-1.5 text-xs" : ""}`}
                disabled={isPendingDelete}
              >
                <Trash className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4 mr-2"} />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}